import React, { useState } from 'react'
import { Steps, Button, Card, Typography, Space, Upload, Alert, Divider } from 'antd'
import { 
  UploadOutlined, 
  EyeOutlined, 
  SettingOutlined, 
  DollarOutlined,
  HomeOutlined,
  BorderOutlined,
  CheckCircleOutlined
} from '@ant-design/icons'
import { useDesignStore } from '../../store/designStore'
import type { UploadProps } from 'antd'

const { Title, Text, Paragraph } = Typography

interface VisualizerWizardProps {
  onFinish: () => void
}

const VisualizerWizard: React.FC<VisualizerWizardProps> = ({ onFinish }) => {
  const [current, setCurrent] = useState(0)
  const [uploadedFile, setUploadedFile] = useState<string | null>(null)
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const { addADUModel, addPoolModel, setBackgroundImage, analyzeImageWithAI } = useDesignStore()

  const steps = [
    {
      title: 'Welcome',
      icon: <HomeOutlined />,
      description: 'Get started with your project'
    },
    {
      title: 'Upload Photo',
      icon: <UploadOutlined />,
      description: 'Upload property photo'
    },
    {
      title: 'Choose Design',
      icon: <SettingOutlined />,
      description: 'Select what to add'
    },
    {
      title: 'Complete',
      icon: <CheckCircleOutlined />,
      description: 'Start designing'
    }
  ]

  const next = () => {
    setCurrent(current + 1)
  }

  const prev = () => {
    setCurrent(current - 1)
  }

  const isHEICFile = (file: File): boolean => {
    const fileName = file.name.toLowerCase()
    const fileType = file.type.toLowerCase()
    
    // Check file extension
    const isHEICExtension = fileName.endsWith('.heic') || fileName.endsWith('.heif')
    
    // Check MIME type (sometimes browsers don't set it correctly)
    const isHEICType = fileType === 'image/heic' || 
                       fileType === 'image/heif' ||
                       fileType === '' // Sometimes HEIC files have empty type
    
    // Check if file starts with HEIC magic bytes in base64
    const isLikelyHEIC = fileName.includes('heic') || fileName.includes('heif')
    
    console.log('HEIC Detection:', {
      fileName,
      fileType,
      isHEICExtension,
      isHEICType,
      isLikelyHEIC
    })
    
    return isHEICExtension || (isHEICType && isLikelyHEIC)
  }

  const convertHEICToJPEG = async (file: File): Promise<File> => {
    // For now, we'll show a helpful error message
    // In a real implementation, you'd use a library like heic2any
    throw new Error('HEIC files are not supported yet. Please convert to JPG or PNG first.')
  }

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options
    
    try {
      console.log('Upload started:', file)
      console.log('File details:', {
        name: (file as File).name,
        size: (file as File).size,
        type: (file as File).type
      })
      
      setUploading(true)
      setUploadError(null) // Clear any previous errors
      
      const fileObj = file as File
      
      // Check for HEIC files and show helpful error
      if (isHEICFile(fileObj)) {
        throw new Error('HEIC/HEIF files are not supported. Please convert your iPhone photo to JPG or PNG format first, or use "Share → Copy Photo" and paste as JPG.')
      }
      
      // Validate file type for supported formats
      const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
      if (!supportedTypes.includes(fileObj.type.toLowerCase())) {
        throw new Error(`Unsupported file type: ${fileObj.type}. Please use JPG, PNG, WebP, or GIF.`)
      }
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (fileObj.size > maxSize) {
        throw new Error('File is too large. Please use an image smaller than 10MB.')
      }
      
      // Create a FileReader to convert to base64
      const reader = new FileReader()
      
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = (e) => {
          const result = e.target?.result as string
          resolve(result)
        }
        reader.onerror = (e) => {
          console.error('FileReader error:', e)
          reject(new Error('Failed to read file'))
        }
        reader.readAsDataURL(fileObj)
      })
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(fileObj)
      console.log('Blob URL created:', blobUrl)
      
      // Get base64 version
      const base64Url = await base64Promise
      console.log('Base64 URL created, length:', base64Url.length)
      
      // Check if the base64 contains HEIC data
      if (base64Url.includes('data:image/heic') || base64Url.includes('data:image/heif')) {
        throw new Error('HEIC/HEIF files detected in base64. Please convert your iPhone photo to JPG format first.')
      }
      
      // Validate that the base64 is actually a supported image
      if (!base64Url.startsWith('data:image/')) {
        throw new Error('File does not appear to be a valid image')
      }
      
      // Additional check for supported formats in base64
      const supportedBase64Types = ['data:image/jpeg', 'data:image/jpg', 'data:image/png', 'data:image/webp', 'data:image/gif']
      const isSupported = supportedBase64Types.some(type => base64Url.startsWith(type))
      
      if (!isSupported) {
        throw new Error('Unsupported image format detected. Please use JPG, PNG, WebP, or GIF.')
      }
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Store the file info and URLs
      setUploadedFile(fileObj.name)
      setUploadedImageUrl(base64Url) // Use base64 directly for better compatibility
      
      // Set as background image for the 3D canvas
      setBackgroundImage(base64Url)
      
      // Analyze image with AI for realistic rendering
      try {
        await analyzeImageWithAI(fileObj)
      } catch (error) {
        console.warn('AI analysis failed, continuing without it:', error)
      }
      
      setUploading(false)
      
      console.log('Upload completed successfully:', {
        fileName: fileObj.name,
        fileType: fileObj.type,
        fileSize: fileObj.size,
        blobUrl: blobUrl,
        base64Length: base64Url.length
      })
      
      // Store blob as backup
      ;(window as any).backupImageUrl = blobUrl
      
      onSuccess?.('Upload successful')
      
    } catch (error) {
      console.error('Upload error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Upload failed'
      setUploadError(errorMessage)
      setUploading(false)
      onError?.(error as Error)
    }
  }

  const handleAddADU = () => {
    addADUModel({
      type: 'ADU',
      name: 'Wizard ADU',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      dimensions: { width: 12, height: 9, depth: 14 },
      materials: { walls: '#f5f5f5', roof: '#8b4513', trim: '#ffffff', door: '#654321', windows: '#87ceeb' },
      sections: [],
      isModular: false,
      features: { bedrooms: 2, bathrooms: 1, kitchen: true, livingRoom: true, dining: false, office: false, storage: true, laundry: false, parking: false, porch: false, balcony: false, fireplace: false },
    })
    next()
  }

  const handleAddPool = () => {
    addPoolModel({
      type: 'Pool',
      name: 'Wizard Pool',
      position: [8, 0, 8],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      dimensions: { width: 16, depth: 8, length: 24 },
      shape: 'rectangular',
      materials: { 
        water: '#4169e1', 
        shell: 'gunite', 
        interior: 'plaster', 
        interiorColor: '#ffffff',
        coping: 'concrete', 
        copingColor: '#d3d3d3', 
        deck: 'concrete', 
        deckColor: '#deb887' 
      },
      features: { 
        spa: true, 
        waterfall: false,
        fountains: false,
        lighting: true, 
        lightingType: 'led',
        lightingColor: 'white',
        heating: true,
        heatingType: 'gas',
        cooling: false,
        filtration: 'sand',
        automation: 'basic',
        cleaner: 'none',
        sanitization: 'chlorine',
        safety: true,
        safetyType: 'fence',
        cover: false,
        coverType: 'automatic',
        sound: false,
        wifi: false,
        excavation: 'standard',
        access: 'easy'
      },
    })
    next()
  }

  const renderStepContent = () => {
    switch (current) {
      case 0:
        return (
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
              <div>
                <Title level={2}>🏠 Welcome to the ADU & Pool Visualizer!</Title>
                <Paragraph style={{ fontSize: '16px', color: '#666' }}>
                  This wizard will guide you through creating your dream ADU or pool design.
                  You'll be able to visualize, customize, and get pricing estimates.
                </Paragraph>
              </div>
              
              <Alert
                message="What you'll accomplish:"
                description={
                  <ul style={{ textAlign: 'left', margin: '8px 0' }}>
                    <li>Upload a photo of your property</li>
                    <li>Add and position ADUs or pools</li>
                    <li>Customize materials and features</li>
                    <li>Get instant pricing estimates</li>
                  </ul>
                }
                type="info"
                showIcon
              />
            </Space>
          </Card>
        )

        case 1:
        return (
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <Title level={3}>📸 Upload Your Property Photo</Title>
                <Paragraph>
                  Upload a clear photo of your backyard or the area where you plan to build.
                  Our AI will help analyze the space for optimal placement.
                </Paragraph>
              </div>
              
                    {/* Error Display */}
                    {uploadError && (
                      <Alert
                        message="Upload Error"
                        description={uploadError}
                        type="error"
                        showIcon
                        closable
                        onClose={() => setUploadError(null)}
                        style={{ marginBottom: '16px' }}
                      />
                    )}

                    {/* Debug info */}
                    <div style={{ fontSize: '12px', color: '#999', textAlign: 'center' }}>
                      Debug: uploading={uploading.toString()}, uploadedFile={uploadedFile || 'null'}, uploadedImageUrl={uploadedImageUrl ? 'set' : 'null'}
                    </div>

              {uploading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Alert
                    message="Processing Your Photo..."
                    description="Our AI is analyzing your property image for optimal placement suggestions."
                    type="info"
                    showIcon
                  />
                </div>
              ) : uploadedFile ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Alert
                    message="Photo Uploaded Successfully!"
                    description={`File: ${uploadedFile}`}
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                  />
                  <div style={{ textAlign: 'center' }}>
                    {uploadedImageUrl ? (
                      <div style={{ 
                        padding: '20px', 
                        background: '#f9f9f9', 
                        borderRadius: '12px',
                        border: '2px dashed #52c41a'
                      }}>
                        <div style={{ marginBottom: '12px' }}>
                          <Text strong>📸 Your Property Photo:</Text>
                        </div>
                        <img 
                          src={uploadedImageUrl} 
                          alt="Uploaded property" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '300px',
                            width: 'auto',
                            height: 'auto',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            border: '3px solid #52c41a',
                            display: 'block',
                            margin: '0 auto',
                            minHeight: '100px',
                            backgroundColor: '#f0f0f0'
                          }}
                          onLoad={(e) => {
                            const img = e.target as HTMLImageElement
                            console.log('✅ Image loaded successfully')
                            console.log('Image details:', {
                              src: img.src,
                              naturalWidth: img.naturalWidth,
                              naturalHeight: img.naturalHeight,
                              displayWidth: img.width,
                              displayHeight: img.height
                            })
                          }}
                          onError={(e) => {
                            const img = e.target as HTMLImageElement
                            console.error('❌ Image failed to load with blob URL')
                            console.error('Failed src:', img.src)
                            
                            // Try fallback to base64
                            const backupUrl = (window as any).backupImageUrl
                            if (backupUrl && img.src !== backupUrl) {
                              console.log('🔄 Trying base64 fallback...')
                              img.src = backupUrl
                            } else {
                              console.error('❌ Both blob and base64 failed')
                              // Show error message
                              img.style.display = 'none'
                              const errorDiv = document.createElement('div')
                              errorDiv.innerHTML = '❌ Failed to load image'
                              errorDiv.style.cssText = 'padding: 40px; background: #ffebee; color: #c62828; border-radius: 8px; text-align: center;'
                              img.parentNode?.insertBefore(errorDiv, img)
                            }
                          }}
                        />
                        <div style={{ marginTop: '12px' }}>
                          <Text type="secondary">✅ Ready for design!</Text>
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#999' }}>
                          URL: {uploadedImageUrl.substring(0, 50)}...
                        </div>
                        <div style={{ marginTop: '8px' }}>
                          <Button 
                            size="small" 
                            type="link"
                            onClick={() => {
                              const backupUrl = (window as any).backupImageUrl
                              if (backupUrl) {
                                console.log('🔄 Switching to base64 URL manually')
                                setUploadedImageUrl(backupUrl)
                              }
                            }}
                          >
                            🔄 Try Base64 Version
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '20px', background: '#f5f5f5', borderRadius: '8px' }}>
                        <Text type="secondary">🔄 Image processing...</Text>
                      </div>
                    )}
                  </div>
                </Space>
              ) : (
                      <Upload.Dragger
                        name="propertyPhoto"
                        multiple={false}
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        customRequest={handleUpload}
                        showUploadList={false}
                        beforeUpload={(file) => {
                          console.log('Before upload:', file)
                          return true
                        }}
                      >
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text" style={{ fontSize: '16px' }}>
                          Click or drag photo to this area to upload
                        </p>
                        <p className="ant-upload-hint" style={{ color: '#666' }}>
                          Support for JPG, PNG, WebP, or GIF images. Maximum file size: 10MB
                        </p>
                        <p className="ant-upload-hint" style={{ color: '#ff6b00', fontSize: '12px', marginTop: '8px' }}>
                          📱 iPhone users: HEIC files not supported. Use "Share → Copy Photo" to convert to JPG
                        </p>
                      </Upload.Dragger>
              )}

              {!uploadedFile && (
                <div style={{ textAlign: 'center' }}>
                  <Button type="link" onClick={next}>
                    Skip for now - I'll upload later
                  </Button>
                  <br />
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => {
                      // Test with a sample blob URL
                      const testUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRlbW8gSW1hZ2U8L3RleHQ+PC9zdmc+'
                      setUploadedFile('demo-image.svg')
                      setUploadedImageUrl(testUrl)
                      console.log('🧪 Test image set:', testUrl)
                    }}
                  >
                    🧪 Test with demo image
                  </Button>
                </div>
              )}
            </Space>
          </Card>
        )

      case 2:
        return (
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <Title level={3}>🏗️ What would you like to design?</Title>
                <Paragraph>
                  Choose what you'd like to add to your property. You can always add more later!
                </Paragraph>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Card
                  hoverable
                  style={{ textAlign: 'center' }}
                  onClick={handleAddADU}
                  cover={
                    <div style={{ padding: '40px', background: '#f0f9ff' }}>
                      <HomeOutlined style={{ fontSize: '64px', color: '#1890ff' }} />
                    </div>
                  }
                >
                  <Card.Meta
                    title="Add an ADU"
                    description="Accessory Dwelling Unit - Perfect for rental income or family living"
                  />
                  <div style={{ marginTop: '12px' }}>
                    <Text type="secondary">• Customizable size & layout</Text><br />
                    <Text type="secondary">• Multiple material options</Text><br />
                    <Text type="secondary">• Instant cost estimates</Text>
                  </div>
                </Card>

                <Card
                  hoverable
                  style={{ textAlign: 'center' }}
                  onClick={handleAddPool}
                  cover={
                    <div style={{ padding: '40px', background: '#f0f9ff' }}>
                      <BorderOutlined style={{ fontSize: '64px', color: '#1890ff' }} />
                    </div>
                  }
                >
                  <Card.Meta
                    title="Add a Pool"
                    description="Swimming pool with optional spa and features"
                  />
                  <div style={{ marginTop: '12px' }}>
                    <Text type="secondary">• Various shapes & sizes</Text><br />
                    <Text type="secondary">• Spa, lighting & heating</Text><br />
                    <Text type="secondary">• Professional installation</Text>
                  </div>
                </Card>
              </div>

              <div style={{ textAlign: 'center' }}>
                <Button type="link" onClick={next}>
                  Skip - I'll add objects later
                </Button>
              </div>
            </Space>
          </Card>
        )

      case 3:
        return (
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
              <div>
                <Title level={2}>🎉 You're All Set!</Title>
                <Paragraph style={{ fontSize: '16px', color: '#666' }}>
                  Your visualizer is ready to use. You can now:
                </Paragraph>
              </div>

              <div style={{ textAlign: 'left', maxWidth: '400px', margin: '0 auto' }}>
                <Alert
                  message="Next Steps:"
                  description={
                    <ul style={{ margin: '8px 0' }}>
                      <li>Use the 3D scene to view your designs</li>
                      <li>Click objects to select and customize them</li>
                      <li>Use the Design Tools panel for detailed editing</li>
                      <li>Add more ADUs or pools as needed</li>
                      <li>Get instant pricing estimates</li>
                    </ul>
                  }
                  type="success"
                  showIcon
                />
              </div>

              <div>
                <Title level={4}>Ready to start designing?</Title>
                <Button type="primary" size="large" onClick={onFinish}>
                  Enter 3D Visualizer
                </Button>
              </div>
            </Space>
          </Card>
        )

      default:
        return null
    }
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Steps current={current} items={steps} style={{ marginBottom: '32px' }} />
      
      <div style={{ marginBottom: '24px' }}>
        {renderStepContent()}
      </div>

      <div style={{ textAlign: 'center' }}>
        <Space>
          {current > 0 && (
            <Button onClick={prev}>
              Previous
            </Button>
          )}
          {current < steps.length - 1 && current !== 2 && (
            <Button type="primary" onClick={next}>
              Next
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button type="primary" onClick={onFinish}>
              Start Designing
            </Button>
          )}
        </Space>
      </div>
    </div>
  )
}

export default VisualizerWizard